import { motion } from 'framer-motion'
import { useLanguage } from '../context/LanguageContext'
import { Target, Users, Zap, Award, Linkedin, Mail, Github, Twitter, Calendar, TrendingUp } from 'lucide-react'
import SEO from '../components/SEO'
import { teamMembers, companyValues, companyMilestones, companyStats } from '../data/teamData'

const About = () => {
  const { t, language } = useLanguage()

  return (
    <>
      <SEO 
        title={t('seo.about.title')}
        description={t('seo.about.description')}
        keywords={t('seo.about.keywords')}
      />
      <div className="mt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="section-title mb-6">
              {t('about.title')} <span className="text-gradient-gold">Contenta.al</span>
            </h1>
            <p className="text-xl text-beige-200 leading-relaxed">
              {t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-display font-bold text-cream mb-6">
                {t('about.story')}
              </h2>
              <div className="space-y-4 text-beige-200 leading-relaxed">
                <p>
                  Born from a bold vision and unstoppable ambition, <span className="text-gold-400 font-semibold">Contenta.al</span> launched in 2026 with a clear mission: to revolutionize digital marketing for businesses worldwide.
                </p>
                <p>
                  What started as a single Instagram page has already begun transforming into a global digital powerhouse. We're not just another marketing agency – we're your partners in building brands that resonate across borders and cultures.
                </p>
                <p>
                  From Albania to the world, we bring fresh perspectives, cutting-edge strategies, and relentless dedication to every project. We're young, hungry, and ready to prove that great ideas have no boundaries.
                </p>
                <p className="text-gold-400 font-semibold italic">
                  This is just the beginning. Watch us grow.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl shadow-gold-lg overflow-hidden">
                <img 
                  src="/about/story.jpg" 
                  alt="Our Story" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 ring-4 ring-gold-400/20 rounded-3xl"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title mb-6">
              {t('about.values')} <span className="text-gradient-gold"></span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-premium text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 text-5xl">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-display font-bold text-cream mb-4">
                  {value.title[language]}
                </h3>
                <p className="text-beige-200">
                  {value.description[language]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {companyStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl font-bold text-gradient-gold mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-beige-200">
                  {stat.label[language]}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title mb-6">
              {language === 'sq' ? 'Rrugëtimi Ynë' : 'Our Journey'}
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-gold-400 via-bronze-600 to-gold-400"></div>
            
            {companyMilestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`mb-12 flex items-center ${index % 2 === 0 ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pl-12' : 'pr-12 text-right'}`}>
                  <div className="card-premium">
                    <div className="text-2xl font-bold text-gold-400 mb-2">
                      {milestone.year}
                    </div>
                    <h3 className="text-xl font-bold text-cream mb-2">
                      {milestone.title[language]}
                    </h3>
                    <p className="text-beige-200 text-sm">
                      {milestone.description[language]}
                    </p>
                  </div>
                </div>
                <div className="w-6 h-6 bg-gradient-gold rounded-full border-4 border-dark z-10 absolute left-1/2 transform -translate-x-1/2"></div>
                <div className="w-1/2"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title mb-6">
              {t('about.team')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-premium text-center group"
              >
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-gold-400 to-bronze-600 flex items-center justify-center text-4xl font-display font-bold text-white shadow-gold">
                  {member.name.charAt(0)}
                </div>
                <h3 className="text-xl font-display font-bold text-cream mb-2">
                  {member.name}
                </h3>
                <p className="text-gold-400 mb-3">
                  {member.position[language]}
                </p>
                <p className="text-sm text-beige-200 mb-4">
                  {member.bio[language]}
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {member.skills.map((skill, idx) => (
                    <span key={idx} className="text-xs bg-bronze-900/50 text-gold-400 px-3 py-1 rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="flex justify-center gap-3">
                  {member.social.linkedin && (
                    <a href={member.social.linkedin} className="w-8 h-8 rounded-full bg-bronze-900/50 hover:bg-gradient-gold hover:text-white transition-all duration-300 flex items-center justify-center text-gold-400">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {member.social.email && (
                    <a href={`mailto:${member.social.email}`} className="w-8 h-8 rounded-full bg-bronze-900/50 hover:bg-gradient-gold hover:text-white transition-all duration-300 flex items-center justify-center text-gold-400">
                      <Mail className="w-4 h-4" />
                    </a>
                  )}
                  {member.social.github && (
                    <a href={member.social.github} className="w-8 h-8 rounded-full bg-bronze-900/50 hover:bg-gradient-gold hover:text-white transition-all duration-300 flex items-center justify-center text-gold-400">
                      <Github className="w-4 h-4" />
                    </a>
                  )}
                  {member.social.twitter && (
                    <a href={member.social.twitter} className="w-8 h-8 rounded-full bg-bronze-900/50 hover:bg-gradient-gold hover:text-white transition-all duration-300 flex items-center justify-center text-gold-400">
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  )
}

export default About
